import React, { useContext, useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ActivityLayout,
  ActivityTimer,
  ActivityProgress,
  useActivityData,
  activityApiService,
  formatTime,
  generateRemarks
} from '../shared';
import { UserContext } from '../../UserContext';
import { fetchActivitiesImage } from '../fetchActivitiesImage';
import './MCQActivity.css';
import MCQActivityImage from './mental-activity.png';
import GoodImage from './good.png';
import CrossImage from './cross.png';
import FinishedImage from './finished.jpeg';

const MCQActivity = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const activityId = searchParams.get("activityId");

  // Use shared activity data hook
  const {
    actualQuestions,
    currQuestion,
    count,
    totalQuestions,
    marksPerQuestion,
    totalMarks,
    questionNumber,
    gainedMarks,
    items,
    studentId,
    setCount,
    setQuestionNumber,
    setCurrQuestion,
    setMarksPerQuestion,
    setItems,
    setGainedMarks,
    countRef,
    totalQuestionsRef,
    progressIncrement,
    questionNumberRef,
    progressCount,
    gainedMarksRef,
    totalTimeTakenOfAllQuestions,
    totalTimeOfAllQuestions,
    completeActivityRef
  } = useActivityData(activityId);

  // MCQ specific state and refs
  const [finishQuestions, setFinishQuestions] = useState(false);
  const [imageData, setImageData] = useState(null);
  const optionRef1 = useRef(null);
  const optionRef2 = useRef(null);
  const optionRef3 = useRef(null);
  const optionRef4 = useRef(null);
  const studentCurrAnswerRef = useRef("");
  const marksObtainedCurrQuestionRef = useRef(0);
  const isCorrectRef = useRef(0);
  const ProgressRef = useRef(null);
  const submitButton = useRef(null);
  const GoodImageRef = useRef(null);
  const CrossImageRef = useRef(null);
  const FinishedImageRef = useRef(null);

  const { userInfo } = useContext(UserContext);
  const navigate = useNavigate();

  // Fetch image data when current question changes
  useEffect(() => {
    if (currQuestion?.questionpicture) {
      fetchActivitiesImage(setImageData, currQuestion.questionpicture);
    }
  }, [currQuestion]);

  useEffect(() => {
    if (finishQuestions) return; // Stop timer if activity is finished
    const intervalId = setInterval(() => {
      countRef.current = countRef.current - 1;
      if (countRef.current === -1) {
        studentCurrAnswerRef.current = "";
        marksObtainedCurrQuestionRef.current = 0;
        isCorrectRef.current = 0;
        submitStudentQuestionResponse();
        if (questionNumberRef.current + 1 >= totalQuestionsRef.current) {
          setFinishQuestions(true);
          setItems([...items, 0]);
          progressCount.current += progressIncrement.current;
          if (ProgressRef.current) ProgressRef.current.style.width = `${progressCount.current}%`;
          if (GoodImageRef.current) GoodImageRef.current.style.display = "none";
          if (CrossImageRef.current) CrossImageRef.current.style.display = "inline-block";
          if (submitButton.current) submitButton.current.style.display = "none";
          if (FinishedImageRef.current) FinishedImageRef.current.style.display = "inline-block";
          submitCompleteActivityResponse();
        } else {
          questionNumberRef.current = questionNumberRef.current + 1;
          setQuestionNumber(questionNumber => questionNumber + 1);
          setCurrQuestion(actualQuestions[questionNumberRef.current]);
          setMarksPerQuestion(actualQuestions[questionNumberRef.current]?.marks);
          setCount(actualQuestions[questionNumberRef.current]?.timealloted);
          countRef.current = actualQuestions[questionNumberRef.current]?.timealloted;
          setItems([...items, 0]);
          progressCount.current += progressIncrement.current;
          if (ProgressRef.current) ProgressRef.current.style.width = `${progressCount.current}%`;
          if (GoodImageRef.current) GoodImageRef.current.style.display = "none";
          if (CrossImageRef.current) CrossImageRef.current.style.display = "inline-block";

          // Fetch image for next question
          fetchActivitiesImage(setImageData, actualQuestions[questionNumberRef.current]?.questionpicture);
        }
      } else {
        setCount(countRef.current);
      }
    }, 1000);
    return () => clearInterval(intervalId);
  }, [questionNumber, count, finishQuestions]);

  const submitStudentQuestionResponse = async () => {
    totalTimeTakenOfAllQuestions.current += currQuestion.timealloted - countRef.current - 1;
    totalTimeOfAllQuestions.current += currQuestion.timealloted;
    try {
      await activityApiService.saveActivityQuestion({
        Question_id: currQuestion.Question_id,
        Student_id: studentId,
        timetaken: currQuestion.timealloted - countRef.current - 1,
        StudentAnswer: studentCurrAnswerRef.current,
        MarksObtained: marksObtainedCurrQuestionRef.current,
        TotalTime: currQuestion.timealloted,
        correct: isCorrectRef.current
      });
      await activityApiService.saveActivity({
        Activity_id: activityId,
        Student_id: studentId,
        MarksObtained: gainedMarksRef.current,
        MarksObtainedPercentage: gainedMarksRef.current / totalMarks * 100,
        Remarks: generateRemarks(gainedMarksRef.current / totalMarks * 100),
        SubmitActivityFile: '',
        TimeTaken: totalTimeTakenOfAllQuestions.current,
        TotalMarks: totalMarks,
        TotalTime: totalTimeOfAllQuestions.current < 0 ? 0 : totalTimeOfAllQuestions.current,
        Complete: 0,
      });
    } catch (error) {
      console.error('Error submitting response:', error);
    }
  };

  const submitCompleteActivityResponse = async () => {
    try {
      await activityApiService.saveActivity({
        Activity_id: activityId,
        Student_id: studentId,
        MarksObtained: gainedMarksRef.current,
        MarksObtainedPercentage: gainedMarksRef.current / totalMarks * 100,
        Remarks: 'Good Efforts',
        SubmitActivityFile: '',
        TimeTaken: totalTimeTakenOfAllQuestions.current,
        TotalMarks: totalMarks,
        TotalTime: totalTimeOfAllQuestions.current,
        Complete: completeActivityRef.current,
      });
    } catch (error) {
      console.error('Error submitting complete activity:', error);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!optionRef1.current?.checked && !optionRef2.current?.checked && !optionRef3.current?.checked && !optionRef4.current?.checked) {
      alert("Please select an answer before submitting!");
      return;
    }

    const isLastQuestion = questionNumber === totalQuestions;
    let isCorrect = false;
    let selectedAnswer = "";

    if (optionRef1.current?.checked && currQuestion.option1 === currQuestion.correctanswer) {
      isCorrect = true;
      selectedAnswer = currQuestion.option1;
    } else if (optionRef2.current?.checked && currQuestion.option2 === currQuestion.correctanswer) {
      isCorrect = true;
      selectedAnswer = currQuestion.option2;
    } else if (optionRef3.current?.checked && currQuestion.option3 === currQuestion.correctanswer) {
      isCorrect = true;
      selectedAnswer = currQuestion.option3;
    } else if (optionRef4.current?.checked && currQuestion.option4 === currQuestion.correctanswer) {
      isCorrect = true;
      selectedAnswer = currQuestion.option4;
    } else {
      if (optionRef1.current?.checked) selectedAnswer = currQuestion.option1;
      else if (optionRef2.current?.checked) selectedAnswer = currQuestion.option2;
      else if (optionRef3.current?.checked) selectedAnswer = currQuestion.option3;
      else if (optionRef4.current?.checked) selectedAnswer = currQuestion.option4;
    }

    studentCurrAnswerRef.current = selectedAnswer;
    marksObtainedCurrQuestionRef.current = isCorrect ? marksPerQuestion : 0;
    isCorrectRef.current = isCorrect ? 1 : 0;

    if (isCorrect) {
      gainedMarksRef.current += marksPerQuestion;
      setGainedMarks(gainedMarks => gainedMarks + marksPerQuestion);
      setItems([...items, 1]);
      if (CrossImageRef.current) CrossImageRef.current.style.display = "none";
      if (GoodImageRef.current) GoodImageRef.current.style.display = "inline-block";
    } else {
      setItems([...items, 0]);
      if (GoodImageRef.current) GoodImageRef.current.style.display = "none";
      if (CrossImageRef.current) CrossImageRef.current.style.display = "inline-block";
    }

    submitStudentQuestionResponse();

    if (isLastQuestion) {
      if ((gainedMarksRef.current / totalMarks * 100) >= 75) {
        completeActivityRef.current = 1;
      }
      setFinishQuestions(true);
      if (submitButton.current) submitButton.current.style.display = "none";
      if (FinishedImageRef.current) FinishedImageRef.current.style.display = "inline-block";
      progressCount.current += progressIncrement.current;
      if (ProgressRef.current) ProgressRef.current.style.width = `${progressCount.current}%`;
      submitCompleteActivityResponse();
    } else {
      questionNumberRef.current += 1;
      setQuestionNumber(questionNumber => questionNumber + 1);
      setCurrQuestion(actualQuestions[questionNumberRef.current]);
      setMarksPerQuestion(actualQuestions[questionNumberRef.current]?.marks);
      setCount(actualQuestions[questionNumberRef.current]?.timealloted);
      countRef.current = actualQuestions[questionNumberRef.current]?.timealloted;
      if (optionRef1.current) optionRef1.current.checked = false;
      if (optionRef2.current) optionRef2.current.checked = false;
      if (optionRef3.current) optionRef3.current.checked = false;
      if (optionRef4.current) optionRef4.current.checked = false;
      progressCount.current += progressIncrement.current;
      if (ProgressRef.current) ProgressRef.current.style.width = `${progressCount.current}%`;

      // Fetch image for next question
      fetchActivitiesImage(setImageData, actualQuestions[questionNumberRef.current]?.questionpicture);
    }
  };

  const onLogout = () => {
    fetch(`${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}/logout`, {
      credentials: 'include',
      method: 'POST',
    });
    navigate("/");
  };

  const handleCourseSelect = (course) => {
    console.log("Selected Course:", course);
  };

  if (!currQuestion && !finishQuestions) {
    return <div>Loading questions...</div>;
  }

  return (
    <ActivityLayout
      title="MCQ Activity"
      onLogout={onLogout}
      onCourseSelect={handleCourseSelect}
    >
      <div className="MAMheading">
        <div className="MAMactivity-image">
          <img src={MCQActivityImage} alt="" />
        </div>

        <div className="MAMactivity-text">
          <h3>{currQuestion?.ActivityName}</h3>
        </div>
      </div>

      <div className="MAMcontainer">
        <div className="MAMbody">
          <div className="left-column">
            <div className="question-tracker">
              <p>
                Question
                <span className="question-number">#{questionNumber}</span>
              </p>
              <ActivityProgress
                current={questionNumber}
                total={totalQuestions}
                progress={(questionNumber / totalQuestions) * 100}
              />
            </div>
            <div className="mobile-timer">
              <ActivityTimer count={count} displayFormat="box" />
            </div>
            <div className="play-area">
              <div className="MAMCQmcq-text">
                <h4 className="mcqqstn">
                  Q:{" "}
                  <span id="MAMCQmental-question-text">
                    {currQuestion?.question}
                  </span>
                </h4>
              </div>
              {currQuestion?.questionpicture ? (
                <img
                  src={currQuestion?.questionpicture}
                  alt="Question Image"
                  className="question-image"
                />
              ) : (
                <div></div>
              )}
              <div className="MAMCQmcq-options">
                <div>
                  <input
                    type="radio"
                    className="mcq-radio"
                    ref={optionRef1}
                    id={"option1"}
                    name="mcq"
                    value={currQuestion?.option1}
                    style={{ width: "20px", marginRight: "20px" }}
                  />
                  <label htmlFor={"option1"} className="mcq-option-label">
                    {" "}
                    {currQuestion?.option1}
                  </label>
                  <br />
                </div>
                <div>
                  <input
                    type="radio"
                    className="mcq-radio"
                    ref={optionRef2}
                    id={"option2"}
                    name="mcq"
                    value={currQuestion?.option2}
                    style={{ width: "20px", marginRight: "20px" }}
                  />
                  <label htmlFor={"option2"} className="mcq-option-label">
                    {" "}
                    {currQuestion?.option2}
                  </label>
                  <br />
                </div>
                <div>
                  <input
                    type="radio"
                    className="mcq-radio"
                    ref={optionRef3}
                    id={"option3"}
                    name="mcq"
                    value={currQuestion?.option3}
                    style={{ width: "20px", marginRight: "20px" }}
                  />
                  <label htmlFor={"option3"} className="mcq-option-label">
                    {" "}
                    {currQuestion?.option3}
                  </label>
                  <br />
                </div>
                {currQuestion?.option4 && <div>
                  <input
                    type="radio"
                    className="mcq-radio"
                    ref={optionRef4}
                    id={"option4"}
                    name="mcq"
                    value={currQuestion?.option4}
                    style={{ width: "20px", marginRight: "20px" }}
                  />
                  <label htmlFor={"option4"} className="mcq-option-label">
                    {" "}
                    {currQuestion?.option4}
                  </label>
                  <br />
                </div>}
              </div>
              <div className="MAMCQplay-button">
                <button
                  ref={submitButton}
                  className="SQAsubmitButton"
                  onClick={handleSubmit}
                  disabled={finishQuestions}
                >
                  Submit
                </button>
              </div>
            </div>
            <div className="MAMpicture">
              {imageData ? (
                <img src={imageData} alt="Question Image" />
              ) : (
                <p></p>
              )}
            </div>
            <div className="MAMCQremarks">
              <div className="MAMCQcorrect-wrong-picture">
                <img
                  id="MAremarksId"
                  src={GoodImage}
                  ref={GoodImageRef}
                  alt=""
                  style={{ width: "100px", display: "none" }}
                />
                <img
                  id="remarksId"
                  src={CrossImage}
                  ref={CrossImageRef}
                  alt=""
                  style={{ width: "100px", display: "none" }}
                />
              </div>

              <div className="MAMCQfinished-picture">
                <img
                  id="finishedId"
                  src={FinishedImage}
                  ref={FinishedImageRef}
                  alt=""
                  style={{ width: "100px", display: "none" }}
                />
              </div>
            </div>
          </div>

          <div className="right-column">
            <div className="desktop-timer">
              <ActivityTimer count={count} displayFormat="box" />
            </div>
            <div id="MAmarks-color">
              <h3 className="MAMscoreHeading">Score</h3>
              <h3 className="MAMscoreHeading" style={{ display: "flex" }}>
                <span id="MAOcurrent-score">{gainedMarks}</span> /
                <span id="MAOtotal-score">{totalMarks}</span>
              </h3>
              {items.map((item, index) => (
                <div className="MAbox" key={index}>
                  <div className="MAbox1">
                    <h3>{(index + 1).toString().padStart(2, "0")}</h3>
                  </div>
                  <div className={`MAbox2${item}`}>
                    {item === 1 ? "✓" : "✗"}
                  </div>
                </div>
              ))}
            </div>
            {currQuestion?.instructions && (
              <div className="">
                <span
                  style={{
                    color: "#b8860b",
                    fontWeight: "bold",
                    cursor: "pointer",
                    borderRadius: "6px",
                    padding: "6px 12px",
                    display: "inline-block",
                    margin: "8px 0",
                    display: "flex"
                  }}
                  onClick={() => {
                    window.alert(currQuestion?.instructions);
                  }}
                >
                  Read instruction
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </ActivityLayout>
  );
};

export default MCQActivity;